import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext, HttpParams} from "@angular/common/http";
import {map, Observable} from "rxjs";
import {DEDUPE_KEY} from "../../x-interceptors/duplicate-request.interceptor";
import {EventDto} from "../../models/event/event-dto";
import {EventDetailsDto, EventDetailsOrder} from "../../models/event/event-details";
import {EventOrderDelta} from "../../models/event/event-order-delta";
import {CompositionGroupingParameter} from "../../models/event/event-order-stats";
import {LocationConfigLinker} from "../../models/location/location-config-linker";
import {Tuple} from "../../models/_generic/tuple";
import {EventNl} from "../../models/event/event-nl";
import {EmployeeEvent} from "../../models/event/employee-event";
import {EmployeeEventResponseNl} from "../../models/event/employee-event-response-nl";
import {EmployeeEventResponseDto} from "../../models/event/employee-event-response-dto";
import {ServerEndpoints} from "../../server.endpoints";
import {EventCreationModel} from "../../_modules/location/_models/event-creation-model";
import {EventCreationAllowance} from "../../models/event/event-creation-allowance";
import {OrderHelper} from "../../_modules/location/_services/order-helper";
import {EventHelper} from "../../_modules/location/_services/event-helper";
import {DayKey} from "../../_shared/time/company-day";
import {EventKind} from "../../enums/event-kind";

@Injectable({
  providedIn: 'root'
})
export class EventHttpService {

  constructor(private httpClient: HttpClient, @Inject("BASE_URL") private baseUrl: string) {
  }

  /**
   * This shard's events in a window.
   *
   * @param kind
   *   Optional (#664). Omitted, the answer carries **both** kinds — which is what the events list page and the
   *   shift maker want, and why it is not the default. Pass {@link EventKind.Real} on a surface that may only
   *   ever offer a trading event, such as the single-event analysis picker.
   *
   *   Sent only when given, rather than as `&kind=` with an empty value: `EventKind.Real` is the ordinal `0`,
   *   so a template-literal that always appended the parameter would turn "no opinion" into `kind=undefined`
   *   for every existing caller, and ASP.NET binds an unparseable enum value as a 400.
   */
  list(first: number = 0, last: number = 0, start: Date | null = null, end: Date | null = null, finished: boolean = false,
       filter: string = "", kind?: EventKind): Observable<EventDto[]> {
    return this.httpClient.get<EventDto[]>(this.baseUrl + ServerEndpoints.Inventory_Event_List + "?first=" +
      first +
      "&last=" + last +
      "&start=" + (start == null ? "" : start?.toISOString()) +
      "&end=" + (end == null ? "" : end?.toISOString()) +
      "&finished=" + finished +
      "&filter=" + filter +
      (kind === undefined ? "" : "&kind=" + kind)
    );
  }

  test() {
    return this.httpClient.get(this.baseUrl + ServerEndpoints.Management_Hangfire_Authorize);
  }

  /**
   * The event-details payload (#716): a PAGE of orders plus the event-wide statistics.
   *
   * **No `?fields=` projection any more (Feature #760 / Story #761).** The endpoint answers a fixed
   * {@link EventDetailsDto} built from what this page and the single-event analysis page actually render, so
   * there is no projection string to keep in step with a template — and no way to lose a field by trimming
   * one. The seven fields this page lost that way (`ModificationLines` #440, `CompositeId` #534,
   * `Kind`/`Public`/`PublicResponseEnd` #664, `OrderStats` #716, `Grouping` and `DeltaCursor` #745) are all
   * properties of that type now.
   *
   * @param take how many of the NEWEST orders to load. `0` (or omitted) means every one of them — which is
   *   what the analysis page asks for and what the page's explicit „Alle" option sends. The statistics never
   *   depend on it, so the tiles read the same number at every page size.
   * @param grouping which grain the server aggregates the article buckets at (#534). Defaults to `'Combined'`,
   *   which is what every caller that has no opinion wants; the event-details page sends the user's actual
   *   preference, because since #722 the „einzeln" rows come from the server rather than from the loaded page.
   */
  getExtendedEvent(id: string, location: string, take: number = 0,
                   grouping: CompositionGroupingParameter = 'Combined'): Observable<EventDetailsDto> {
    const params = new HttpParams()
      .set('id', id)
      .set('location', location)
      .set('take', take)
      .set('grouping', grouping);

    return this.httpClient.get<EventDetailsDto>(
      this.baseUrl + ServerEndpoints.Inventory_Event_GetExtendedEvent, {params}
    ).pipe(map(event => this.prepare(event)));
  }

  /**
   * The per-payload client-side derivations both full-load paths share: normalisation, the per-order
   * transaction-type amounts, and the four event-wide dictionaries `EventHelper` folds (or, since #716, reads
   * straight off the server's statistics).
   *
   * One place rather than two identical blocks, so a snapshot that arrives from a price-profile save is
   * indistinguishable from one that arrives from a load.
   */
  private prepare(event: EventDetailsDto): EventDetailsDto {
    event.orders ??= [];
    event.orders.forEach(order => {
      EventHttpService.normalizeOrder(order);
      OrderHelper.setOrderBookingValues(order);
    });

    EventHelper.setEventBookingValues(event);
    EventHelper.setOtherCompositionValues(event);
    EventHelper.setNormalCompositionValues(event);
    EventHelper.setLocationValues(event);

    return event;
  }

  /**
   * Fills in the collections the rest of the client dereferences **without a guard**, for one transaction as
   * it comes off the wire.
   *
   * ### Why this exists even though the response DTO declares them required
   *
   * The declaration is a statement of INTENT about a contract the client does not control. Bug #440 is the
   * proof: `composition.modificationLines.length` was read on every change-detection pass of
   * `/location/event/summary` against a payload that did not carry the property, and the page threw
   * `TypeError: Cannot read properties of undefined (reading 'length')` — on every SignalR-driven live
   * refresh, not just the first render. A required TypeScript property cannot stop that; it only means the
   * compiler will not ask anyone to check. (That is also why the template's `?? []` guard emitted `NG8102` as
   * redundant once the field became required — the compiler was reasoning about the type, not the wire.)
   *
   * So the guard moves HERE, to the one seam where the wire meets the client, and runs once per payload
   * instead of once per change-detection pass. Everything downstream — the template, `EventHelper`,
   * `OrderHelper`, the analysis page's `SalesUtils` — may then dereference these freely, which is what makes
   * the required type honest rather than aspirational.
   *
   * ### What is normalised, and the rule that decides it
   *
   * **Exactly the collections some consumer dereferences unguarded**, and nothing else:
   *
   * - `order.compositions` — `@for (composition of order.compositions…)` in the table, `.map(…)` in
   *   `OrderHelper.setOrderBookingValues`, `.flatMap(…)` in the analysis page.
   * - `order.businessCaseInformations` — `.forEach(…)` in `OrderHelper.setOrderBookingValues` and
   *   `.reduce(…)` in `BookingCalculation.businessCaseTotalSigned`.
   * - `line.modificationLines` — `.length` in the template, and `SalesUtils` assigns it straight onto a
   *   `SalesDto` whose consumers iterate it. This is the #440 field itself.
   *
   * Deliberately NOT normalised, each for a reason:
   *
   * - **`compositeId`** — already has a DESIGNED degradation: `compositionVariantKey` maps a missing or blank
   *   id to the plain composition id, i.e. to combined counting (#534). Defaulting it here would do the same
   *   thing while implying the field is optional, and would hide the malformed payload from the one place
   *   that reports it.
   * - **`composition`** (the article object) — a missing one is not a trimmed field, it is a broken line.
   *   Substituting an empty article would render a nameless row and swallow the fault; `other` reading
   *   `undefined` as falsy already lands such a line in „Artikel", which is the safe default.
   * - **`orderStats` / `deltaCursor` / `receipt` / `foreignTransactionId` / `compositionType`** — genuinely
   *   optional server-side (`NullValueHandling.Ignore` omits a null property entirely), declared optional on
   *   the client, and every consumer already branches on their absence. Filling them in would invent data.
   * - **`bookingValues` and the four event-wide dictionaries** — never on the wire at all. They are this
   *   service's own output, assigned a few lines above; guarding them would be guarding against ourselves.
   */
  private static normalizeOrder(order: EventDetailsOrder): EventDetailsOrder {
    // `??=` rather than `|| =`: it catches BOTH an absent key (a trimmed or older payload) and an explicit
    // `null`, and leaves a legitimately empty array alone.
    order.compositions ??= [];
    order.businessCaseInformations ??= [];
    order.compositions.forEach(line => line.modificationLines ??= []);
    return order;
  }

  /**
   * What changed on an event since `cursor` (#716 / Task #723) — the live view's incremental read, in place
   * of refetching the whole event on every sale.
   *
   * @param id the event.
   * @param location the sales-point filter; must be the one the page was loaded with, or the server's idea
   *   of "visible here" differs from the client's and orders would look removed when they are merely filtered.
   * @param cursor the opaque token from the previous answer (or from {@link getExtendedEvent}'s `deltaCursor`
   *   on the full load that seeded the stream). Anything the server cannot read is answered with
   *   `fullReloadRequired`, never with the whole event, so an empty string is safe.
   *
   * **Its rows are the SAME {@link EventDetailsOrder} a full snapshot carries** (Story #762), and that is a
   * correctness requirement rather than tidiness: the component merges them into the snapshot's list BY ID,
   * so a delta answering a different shape would leave one list holding two kinds of row.
   *
   * The per-order derivation is applied here, exactly as in {@link getExtendedEvent}, so a merged order is
   * indistinguishable from one that arrived in a full snapshot. The EVENT-level aggregates are not: they
   * depend on the merged list plus the server statistics, and are rebuilt by the component after the merge.
   */
  getEventDelta(id: string, location: string, cursor: string,
                grouping: CompositionGroupingParameter = 'Combined'): Observable<EventOrderDelta> {
    const params = new HttpParams()
      .set('id', id)
      .set('location', location)
      .set('cursor', cursor)
      // The delta's statistics are the full tiles on every response, so they have to be aggregated at the SAME
      // grain the snapshot was — otherwise the first live update after a page load re-buckets the cards.
      .set('grouping', grouping);

    return this.httpClient.get<EventOrderDelta>(
      this.baseUrl + ServerEndpoints.Inventory_Event_GetEventDelta, {params}
    ).pipe(map(delta => {
      // Normalised on the SAME terms as a snapshot's rows (#762): these are merged into that list by id, so a
      // delta row that skipped the boundary would be the one row in the table able to throw.
      delta.changed ??= [];
      delta.removedOrderIds ??= [];
      delta.changed.forEach(order => {
        EventHttpService.normalizeOrder(order);
        OrderHelper.setOrderBookingValues(order);
      });
      return delta;
    }));
  }

  getCurrentOrNext(): Observable<Tuple<EventDto, boolean> | undefined> {
    return this.httpClient.get<Tuple<EventDto, boolean> | undefined>(this.baseUrl + ServerEndpoints.Inventory_Event_GetCurrentOrNext);
  }

  getEvent(id: string): Observable<EventDto> {
    return this.httpClient.get<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_Get + "?id=" + id);
  }

  changeName(id: string, name: string): Observable<EventDto> {
    return this.httpClient.put<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_ChangeName, {
      Id: id,
      Name: name
    })
  }


  add(name: string, start: Date, end: Date): Observable<EventDto> {
    return this.httpClient.post<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_Add, {
      "Name": name,
      "Start": start.toISOString(),
      "End": end.toISOString()
    })
  }

  /**
   * Creates a batch of events — or, with `dryRun`, asks what creating them *would* produce (#664).
   *
   * A dry run persists nothing and answers with the would-be events, each carrying its **resolved** kind, so
   * the series preview can show which occurrences a contract boundary turns into virtual ones before anything
   * is written. A refusal comes back as the same structured `ApiError` the real call answers with, so both
   * paths map through the one vocabulary in `event-creation-errors.ts`.
   */
  addMany(events: EventCreationModel[], dryRun: boolean = false): Observable<EventDto[]> {
    const url = `${this.baseUrl}${ServerEndpoints.Inventory_Event_AddMany}${dryRun ? '?dryRun=true' : ''}`;
    return this.httpClient.put<EventDto[]>(url, events);
  }

  /**
   * Asks what may be created in `[start, end]` — see {@link EventCreationAllowance} (#664).
   *
   * The bounds are serialised with `toISOString()`, the same shape `changeTime`/`add` already send, so the
   * server receives one unambiguous instant per bound and applies its own company-local date rules to it.
   */
  getCreationAllowance(start: Date, end: Date): Observable<EventCreationAllowance> {
    const params = new HttpParams()
      .set('start', start.toISOString())
      .set('end', end.toISOString());
    return this.httpClient.get<EventCreationAllowance>(
      this.baseUrl + ServerEndpoints.Inventory_Event_GetCreationAllowance, {params});
  }

  delete(id: string) {
    return this.httpClient.delete(this.baseUrl + ServerEndpoints.Inventory_Event_Delete + "?id=" + id);
  }

  upsert(event: EventDto): Observable<EventDto> {
    return this.httpClient.put<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_Upsert, JSON.stringify(event));
  }

  download(id: string, supplier: string) {
    return this.httpClient.get(this.baseUrl + ServerEndpoints.Inventory_Event_Download + "?id=" + id + "&supplierId=" + supplier, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob'
    });
  }

  downloads(ids: string[], supplier: string) {
    return this.httpClient.post(this.baseUrl + ServerEndpoints.Inventory_Event_Downloads + "?supplierId=" + supplier, ids, {
      reportProgress: true,
      observe: 'events',
      responseType: 'blob'
    })
  }

  /**
   * Saves the price profiles and answers with the refreshed extended event.
   *
   * @param take the caller's current page size — see {@link getExtendedEvent}. Passed through so saving a
   *   profile does not quietly answer with the unbounded order list the page just stopped asking for (#716).
   * @param grouping the caller's article-bucket grain — see {@link getExtendedEvent}. Passed through for the
   *   same reason: the answer replaces the snapshot on screen, cards included.
   */
  setLocationConfigs(id: string, locationConfigs: LocationConfigLinker[],
                     take: number = 0,
                     grouping: CompositionGroupingParameter = 'Combined'): Observable<EventDetailsDto> {
    let linkers: any[] = [];
    locationConfigs.forEach(l => {
      linkers.push({
        "Location": l.location.id,
        "Configuration": l.configuration?.id
      })
    })

    const params = new HttpParams()
      .set('eventId', id)
      .set('take', take)
      .set('grouping', grouping);

    // Answers the same {@link EventDetailsDto} `getExtendedEvent` does — it IS that read, run after the save.
    return this.httpClient.put<EventDetailsDto>(
      this.baseUrl + ServerEndpoints.Inventory_Event_SetLocationConfigs, linkers, {params}
    ).pipe(map(event => this.prepare(event)));
  }

  changeTime(id: string, start: Date, end: Date): Observable<EventDto> {
    // Name is id in backend
    return this.httpClient.put<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_ChangeTime, {
      Id: id,
      Start: start.toISOString(),
      End: end.toISOString()
    })
  }

  setPublicProperty(id: string, name: string, value: string): Observable<EventDto> {
    return this.httpClient.put<EventDto>(this.baseUrl + ServerEndpoints.Inventory_Event_SetPublicVariable + "?id=" + id, {
      name: name,
      value: value
    })
  }

  /**
   * Loads the employee calendar's public events for `[start, end)`, with the employee's own availability
   * answers attached only to events starting at or after `responsesFrom`.
   *
   * **Two windows of different widths, on purpose.** The month picker draws a DOT per event and needs the
   * whole visible range — including months in the past, which is why the events window follows the picker.
   * The event LIST starts at the selected day and is the only consumer of the per-employee response rows, so
   * the responses window follows the list. Collapsing them into one is what made past months' dots vanish.
   *
   * Everything is bounded below. This call used to send no parameters at all, so the server answered with the
   * company's ENTIRE public-event history on every page load — 599 events / 425 KB / ~5 s on one production
   * tenant. Every bound is an INSTANT, sent verbatim as ISO-8601 — the same shape the sibling
   * `ShiftHttpService.getShiftOverview` on this screen uses. A calendar-day bound is the caller's to resolve
   * (`companyDayStartInstant(...)`, see `EventService`): this method used to round to BROWSER-local midnight,
   * which made the window depend on the viewer's zone (#656). An omitted bound lets the server apply its
   * company-local default (current month for `start`, current day for `responsesFrom`, unbounded above for `end`).
   */
  getPublicEvents(start: Date | null = null, end: Date | null = null,
                  responsesFrom: Date | null = null): Observable<EmployeeEvent[]> {
    let params = new HttpParams();
    if (start) params = params.set('start', start.toISOString());
    if (end) params = params.set('end', end.toISOString());
    if (responsesFrom) params = params.set('responsesFrom', responsesFrom.toISOString());

    return this.httpClient.get<EmployeeEvent[]>(
      this.baseUrl + ServerEndpoints.Inventory_Event_GetPublicEvents, {params});
  }

  sendEmployeeEventResponse(shard: string, ev: string, response: EmployeeEventResponseNl): Observable<EmployeeEventResponseNl> {
    // Dedupe an availability submission on the (shard, event) it answers rather than the response body — the
    // body carries a mutable state flag / timestamps that can differ between two rapid clicks on "save".
    const context = new HttpContext().set(DEDUPE_KEY, `employee-availability:${shard}:${ev}`);
    return this.httpClient.put<EmployeeEventResponseNl>(this.baseUrl + ServerEndpoints.Inventory_Event_SendEmployeeEventResponse + "?shard=" + shard + "&ev=" + ev,
      response,
      {context}
    )
  }

  changePublicResponseTime(id: string, time: Date, shard: string | undefined = undefined): Observable<EventDto> {
    let url = this.baseUrl + ServerEndpoints.Inventory_Event_ChangePublicResponseTime + "?id=" + id + "&time=" + time.toISOString();
    if (shard) {
      url += "&shard=" + shard;
    }

    return this.httpClient.put<EventDto>(url, {});
  }

  /**
   * The shift maker's availability board for the company month containing `day` (`yyyy-MM-dd`). The day is sent
   * verbatim and resolved in the company zone (#651) — an ISO instant of browser-local midnight fell into the
   * previous UTC month for browsers east of the company zone on the 1st.
   */
  getEmployeeAvailability(day: DayKey): Observable<Tuple<EventNl[], EmployeeEventResponseDto[]>> {
    const params = new HttpParams().set('date', day);
    return this.httpClient.get<Tuple<EventNl[], EmployeeEventResponseDto[]>>(this.baseUrl + ServerEndpoints.Inventory_Event_GetEmployeeAvailability, {params});
  }

  /**
   * The shift maker's calendar dots: events whose start lies on a company calendar day in `[start, end]`
   * (`yyyy-MM-dd`, END INCLUSIVE — pass the last visible picker day, the client adds no day). The server resolves
   * the days in the company zone.
   */
  getPrivatePublicEvents(start: DayKey, end: DayKey): Observable<EventNl[]> {
    const params = new HttpParams().set('start', start).set('end', end);
    return this.httpClient.get<EventNl[]>(this.baseUrl + ServerEndpoints.Inventory_Event_GetPrivatePublicEvents, {params});
  }

  /** The locations disabled on the company calendar day `day` (`yyyy-MM-dd`), resolved in the company zone. */
  getDisabledLocations(day: DayKey): Observable<any> {
    const params = new HttpParams().set('date', day);
    return this.httpClient.get<any>(this.baseUrl + ServerEndpoints.Inventory_Event_GetDisabledLocations, {params});
  }
}
